import { EcommerceAPIApi as EcommerceApi, RateOutput, SpecialRateTicketSaveRequest, SpecialRateTicketUpdateSaveRequest, SpecialRateTicketPaymentSaveRequest, SpecialRateTickets, SpecialRateProgramUsage, DBTicket, Reservation, ReservationsDiagnostics, CreateReservationByEventSaveRequest, CreateReservationUsingEventSourceEnum, DownloadReservationsImportTemplateSourceEnum, ReservationsImportDiagnosticsSourceEnum, ReservationsImportUploadSourceEnum, UserProfile, EventDetails } from '@parkingbase/ecommerce-api-test';
import type { TicketSearchResult } from '@/components/myaccount/parking-validation/change-discount/ChangeDiscount.types';
import type { RawAxiosRequestConfig } from 'axios';
import { getFileNameFromDispositionHeader } from '@/lib/file-download';
import { PAYMENT_TIMEOUT_MS } from '@/lib/api/payment-timeout';
import { BaseApiService } from './baseApi.service';

/** Used only when the server omits `Content-Disposition`. */
const DEFAULT_IMPORT_TEMPLATE_FILE_NAME = 'reservation-import-template.xlsx';

/** An import template plus the name the server said to save it under. */
export interface ImportTemplateDownload {
  blob: Blob;
  fileName: string;
}

/**
 * Actual runtime shape of GET /corporateValidation/search/ticket/new.
 *
 * ## Invariants:
 * - The generated `@parkingbase/ecommerce-api-test` client mistypes this endpoint's
 *   response as `DBTicket`, but the backend returns a TicketSearchResponse
 *   *wrapper*. The legacy reservation site reads it the same way
 *   (vm-reservation-website HomePage.tsx:820 — `data.ticket`, `data.rateOutputs`,
 *   `data.specialRatePrograms`, `data.chargeToAccountEnabled`). Do not treat the
 *   response as a flat DBTicket.
 */
interface SearchTicketNewResponse {
  ticket?: DBTicket;
  rateOutputs?: TicketSearchResult['rateOutputs'];
  specialRatePrograms?: TicketSearchResult['specialRatePrograms'];
  chargeToAccountEnabled?: boolean;
  company?: string;
}

export class ParkingValidationService extends BaseApiService {
  protected corporateValidationApi: EcommerceApi;
  protected reservationApi: EcommerceApi;
  protected dataImportApi: EcommerceApi;
  protected userDiscountProgramApi: EcommerceApi;
  protected ticketApi: EcommerceApi;
  protected rateEngineApi: EcommerceApi;

  constructor() {
    super();
    this.corporateValidationApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
    this.reservationApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
    this.dataImportApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
    this.userDiscountProgramApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
    this.ticketApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
    this.rateEngineApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
  }

  // ── Active Tickets ──────────────────────────────────────────────────────

  async getTickets(options?: RawAxiosRequestConfig): Promise<SpecialRateTickets[]> {
    const response = await this.corporateValidationApi.getTicket1(options);
    return response.data;
  }

  async searchTicket(
    ticketNumber?: string,
    ticketId?: string,
    licensePlate?: string,
    options?: RawAxiosRequestConfig
  ): Promise<DBTicket> {
    const response = await this.corporateValidationApi.searchTicketNew(
      ticketNumber,
      ticketId,
      licensePlate,
      options
    );
    return response.data;
  }

  async searchTicketNew(
    ticketNumber?: string,
    ticketId?: string,
    licensePlate?: string,
    options?: RawAxiosRequestConfig
  ): Promise<TicketSearchResult | null> {
    const { data } = await this.corporateValidationApi.searchTicketNew(
      ticketNumber,
      ticketId,
      licensePlate,
      options
    );
    // The generated client types this response as DBTicket, but the backend
    // returns a { ticket, rateOutputs, specialRatePrograms, chargeToAccountEnabled,
    // company } wrapper (same as the legacy site). Flatten ticket + arrays into a
    // TicketSearchResult so downstream consumers can read DBTicket fields directly.
    const response = data as unknown as SearchTicketNewResponse;
    if (!response?.ticket) return null;
    return {
      ...response.ticket,
      specialRatePrograms: response.specialRatePrograms,
      rateOutputs: response.rateOutputs,
      chargeToAccountEnabled: response.chargeToAccountEnabled,
    };
  }

  /**
   * Computes the live rate for an open ticket up to a given exit time
   * (GET /rate-engine/open-ticket/rate). Used by the Charge-to-Account flow to
   * recalculate the rate when an account is selected and when the exit time changes
   * (matches the legacy reservation site's fetchRate).
   */
  async getRateForOpenTicket(
    ticketId: string,
    exitBy: string,
    company?: string,
    options?: RawAxiosRequestConfig
  ): Promise<RateOutput> {
    const { data } = await this.rateEngineApi.getRateForOpenTicket(
      ticketId,
      exitBy,
      company,
      options
    );
    return data;
  }

  async addTicket(
    request: SpecialRateTicketSaveRequest,
    options?: RawAxiosRequestConfig
  ): Promise<DBTicket> {
    const response = await this.corporateValidationApi.addTicket(request, options);
    return response.data;
  }

  async updateTicket(
    request: SpecialRateTicketUpdateSaveRequest,
    options?: RawAxiosRequestConfig
  ): Promise<DBTicket> {
    const response = await this.corporateValidationApi.updateTicket(request, options);
    return response.data;
  }

  async payTicket(
    request: SpecialRateTicketPaymentSaveRequest,
    options?: RawAxiosRequestConfig
  ): Promise<DBTicket> {
    const response = await this.corporateValidationApi.payTicket(request, { ...options, timeout: PAYMENT_TIMEOUT_MS });
    return response.data;
  }

  // ── Reports ─────────────────────────────────────────────────────────────

  async getTicketsByMonth(
    month?: string,
    options?: RawAxiosRequestConfig
  ): Promise<SpecialRateTickets[]> {
    const response = await this.corporateValidationApi.getTicketsByMonth(month, options);
    return response.data;
  }

  // Return the full AxiosResponse (not just .data) so callers can read the
  // server's Content-Disposition filename via downloadFileFromResponse — matches
  // the legacy reservation app's download standard. PB-3126.
  async exportPdf(month?: string, options?: RawAxiosRequestConfig) {
    return this.corporateValidationApi.getTicketPdf(month, {
      responseType: 'blob',
      ...options,
      headers: { ...options?.headers },
    });
  }

  async exportXlsx(month?: string, options?: RawAxiosRequestConfig) {
    return this.corporateValidationApi.getTicketExport(month, {
      responseType: 'blob',
      ...options,
      headers: { ...options?.headers },
    });
  }

  // ── Usage ───────────────────────────────────────────────────────────────

  async getSpecialRateUsage(options?: RawAxiosRequestConfig): Promise<SpecialRateProgramUsage[]> {
    const response = await this.corporateValidationApi.getSpecialRateUsage(options);
    return response.data;
  }

  // ── Events ──────────────────────────────────────────────────────────────

  async getEventsForUser(options?: RawAxiosRequestConfig): Promise<EventDetails[]> {
    const response = await this.reservationApi.getEventsForUser(options);
    const parsed = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
    return Object.values(parsed).flat() as EventDetails[];
  }

  // ── Event Reservations ──────────────────────────────────────────────────

  async getReservationsByEvent(
    start: string,
    end: string,
    eventId?: string,
    options?: RawAxiosRequestConfig
  ): Promise<Reservation[]> {
    const response = await this.reservationApi.getReservationsByEventsForUser(
      start,
      end,
      eventId,
      options
    );
    const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
    // API may return a map keyed by eventId (same pattern as getEventsForUser) — flatten to array
    if (Array.isArray(data)) {
      return data;
    }
    return Object.values(data).flat() as Reservation[];
  }

  async createReservationByEvent(
    source: CreateReservationUsingEventSourceEnum,
    request: CreateReservationByEventSaveRequest,
    options?: RawAxiosRequestConfig
  ): Promise<Reservation> {
    const response = await this.reservationApi.createReservationUsingEvent(source, request, options);
    return response.data;
  }

  async updateReservation(
    reservationId: string,
    reservation: Reservation,
    options?: RawAxiosRequestConfig
  ): Promise<void> {
    await this.reservationApi.updateEcommerceReservation(reservationId, reservation, options);
  }

  async cancelReservation(reservationId: string, options?: RawAxiosRequestConfig): Promise<void> {
    // PV cancels a single reservation (never a whole group), so wholeGroup stays undefined;
    // the axios config is now the 3rd arg after the client added the `wholeGroup` parameter.
    await this.reservationApi.cancelCustomerReservation(reservationId, undefined, options);
  }

  // ── Import Reservations ─────────────────────────────────────────────────

  /**
   * The name has to come back with the blob: the server already builds it from the event's garage
   * and the generated date (`ReservationController.downloadReservationsImportTemplate`), and
   * returning `response.data` alone threw that away (PB-4565).
   */
  async downloadImportTemplate(
    company: string,
    eventId: string,
    options?: RawAxiosRequestConfig
  ): Promise<ImportTemplateDownload> {
    const response = await this.reservationApi.downloadReservationsImportTemplate(
      company,
      DownloadReservationsImportTemplateSourceEnum.RESERVATION,
      eventId,
      true,
      { responseType: 'blob', ...options, headers: { ...options?.headers } }
    );
    return {
      blob: response.data,
      fileName: getFileNameFromDispositionHeader(response, DEFAULT_IMPORT_TEMPLATE_FILE_NAME),
    };
  }

  async runImportDiagnostics(
    company: string,
    file: File[],
    garageId?: string,
    eventId?: string,
    options?: RawAxiosRequestConfig
  ): Promise<ReservationsDiagnostics[]> {
    const response = await this.dataImportApi.reservationsImportDiagnostics(
      company,
      ReservationsImportDiagnosticsSourceEnum.RESERVATION,
      file,
      garageId,
      eventId,
      false,
      options
    );
    return response.data;
  }

  async executeImport(
    company: string,
    diagnostics: ReservationsDiagnostics[],
    garageId?: string,
    eventId?: string,
    options?: RawAxiosRequestConfig
  ): Promise<void> {
    await this.dataImportApi.reservationsImportUpload(
      company,
      ReservationsImportUploadSourceEnum.RESERVATION,
      diagnostics,
      garageId,
      eventId,
      options
    );
  }

  // ── Program User Management ─────────────────────────────────────────────

  async getUsersByDiscountProgram(
    companyId: string,
    discountProgram?: string,
    options?: RawAxiosRequestConfig
  ): Promise<UserProfile[]> {
    const response = await this.userDiscountProgramApi.getUsersByDiscountProgram(
      companyId,
      discountProgram,
      options
    );
    return response.data;
  }

  async addUserDiscountProgram(
    companyId: string,
    userId: string,
    roles: string[],
    discountProgram?: string,
    options?: RawAxiosRequestConfig
  ): Promise<UserProfile> {
    const response = await this.userDiscountProgramApi.addUserDiscountProgram(
      companyId,
      userId,
      roles,
      discountProgram,
      options
    );
    return response.data;
  }

  async updateUserDiscountProgramRoles(
    companyId: string,
    userId: string,
    roles: string[],
    discountProgram?: string,
    options?: RawAxiosRequestConfig
  ): Promise<UserProfile> {
    const response = await this.userDiscountProgramApi.updateUserDiscountProgramRoles(
      companyId,
      userId,
      roles,
      discountProgram,
      options
    );
    return response.data;
  }

  async removeUserDiscountProgram(
    companyId: string,
    userId: string,
    discountProgram?: string,
    options?: RawAxiosRequestConfig
  ): Promise<UserProfile> {
    const response = await this.userDiscountProgramApi.removeUserDiscountProgram(
      companyId,
      userId,
      discountProgram,
      options
    );
    return response.data;
  }

  // ── Charge to Account ──────────────────────────────────────────────────

  async chargeToAccount(
    ticketId: string,
    accountId: string,
    amount: number,
    exitBy: string,
    company?: string,
    options?: RawAxiosRequestConfig
  ): Promise<DBTicket> {
    const response = await this.ticketApi.chargeToAccount(
      ticketId,
      accountId,
      amount,
      exitBy,
      company,
      options
    );
    return response.data;
  }
}
